import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Guid } from "@/Common/Models/Guid";
import { ServiceComboItemType } from "@/Common/Models/Services/Enums/ServiceComboItemType";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModelT } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { ITableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { IComboOrServiceItem } from "./BaseServiceDefinitionModel";
import { ServiceComboDefinitionModel } from "./ServiceComboDefinitionModel";
import { ServiceDefinitionModel } from "./ServiceDefinitionModel";

class ServiceComboDefinitionItemDTO {
    public ComboId: number;
    public Position: number;
    public ServiceDefinitionId: Guid;
    public IsPrimaryService: boolean;
}

class ServiceOrComboTableItem<TModel extends IComboOrServiceItem> implements ITableDataSourceItem<TModel> {
    public Model: TModel;
    public IsSelected: boolean;
    public Type: ServiceComboItemType;
    public Name: string;
    public Site: string;
    public SiteId: string;
    public Speciality: string;
    public SpecialityId: string;
    public TypeString: string;
    public Duration: number;
    public Price: number;
    public EmployeeIds: string[];
    public NbEmployees: number;
    public ServiceDefinitionId: Guid;
    public ComboId: number;
    public AnySelected: boolean;
    public IsAdHoc: boolean;
    public IsUrgent: boolean;

    constructor(item: TModel) {
        this.Model = item;
        this.IsSelected = false;
        this.Name = item.Name;

        if (item instanceof ServiceDefinitionModel) {
            let castItem: ServiceDefinitionModel = item as ServiceDefinitionModel;
            this.Type = ServiceComboItemType.Service;
            this.Name = castItem.Name;
            this.Site = castItem.GetSite().Model.ClinicalName;
            this.SiteId = castItem.GetSite().Model.Id;
            this.SpecialityId = item.Model.SpecializationLookupId;
            this.Speciality = cacheStore.GetLocalizedValueByLookupId(castItem.Model.SpecializationLookupId);
            this.TypeString = cacheStore.GetString("ServiceLabel");
            let minutes = 0;
            let durationTicks = castItem.Model.DefaultDuration;
            if (durationTicks != null && !isNaN(Number(durationTicks)))
                minutes = Number(durationTicks / Number(600000000));
            this.Duration = minutes;
            this.Price = castItem.Model.Price;
            this.EmployeeIds = castItem.Employees.map(({ ContactFacetId }) => ContactFacetId);
            this.NbEmployees = castItem.Employees.length;
            this.ServiceDefinitionId = castItem.Model.Id;
            this.IsAdHoc = castItem.IsAdHoc;
            this.IsUrgent = castItem.GetUrgency();
        }
        else if (item instanceof ServiceComboDefinitionModel) {
            let castItem = item as ServiceComboDefinitionModel;
            this.Type = ServiceComboItemType.Combo;
            this.Name = castItem.Name;
            this.ComboId = castItem.Model.ComboId;
            this.Site = cacheStore.GetSiteById(castItem.Model.SiteId).Model.ClinicalName;
            this.SiteId = cacheStore.GetSiteById(castItem.Model.SiteId).Model.Id;
            this.SpecialityId = item.Model.SpecializationLookupId;
            this.Speciality = cacheStore.GetLocalizedValueByLookupId(castItem.Model.SpecializationLookupId);
            this.TypeString = cacheStore.GetString("ComboLabel");
            this.IsAdHoc = false;
            this.IsUrgent = castItem.GetUrgency();
            let minutes = 0;
            let totalDurationTicks: number = 0;

            for (const serviceDef of castItem.ServiceDefinitions)
                totalDurationTicks += serviceDef.Model.DefaultDuration;

            if (totalDurationTicks != null && !isNaN(Number(totalDurationTicks)))
                minutes = Number(totalDurationTicks / Number(600000000));
            this.Duration = minutes;

            this.Price = item.Model.Price;
            if (this.Price == null)
                this.Price = 0;

            var employeeIds: string[] = [];
            castItem.ServiceDefinitions.forEach(x => {
                employeeIds = employeeIds.concat(x.Employees.map(x => x.ContactFacetId));
            });
            this.EmployeeIds = employeeIds.distinct();
            this.NbEmployees = this.EmployeeIds.length;
            this.ServiceDefinitionId = castItem.PrimaryServiceDefinitionId;
        }
        else {
            console.log("Item not a service definition or combo model.");
        }
    }

    public ToCheckableUIModel(): CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>> {
        var comboOrService = new CheckboxUIModelT<ServiceOrComboTableItem<IComboOrServiceItem>>();

        comboOrService.Model = this;
        comboOrService.Name = this.Name;
        comboOrService.Id = GuidHelper.NewGuid();
        comboOrService.Target = CheckboxTarget.Unassigned;
        comboOrService.Checked = false;

        return comboOrService;
    }
}

export { ServiceComboDefinitionItemDTO, ServiceOrComboTableItem };
